import moment from 'moment';
import template from './pOvertimeApplications.html';

export function pOvertimeApplicationsDirective() {
  'ngInject';

  const directive = {
    restrict: 'E',
    template: template,
    controller: pOvertimeApplicationsController,
    controllerAs: 'pOvertimeApplications',
    bindToController: true,
  };

  return directive;
}

const ROLE_ORDER = { "TeamLeader": 0, "DeputyTeamLeader": 1 };

class pOvertimeApplicationsController {
  constructor($location, $interval, $scope, $q, $filter, Auth, Notes, OvertimeApplications, Stats, Work, $translate, ConnectionQuality) {
    'ngInject';

    this.$onInit = () => {
      this.$location = $location;
      this.$interval = $interval;
      this.$scope = $scope;
      this.$q = $q;
      this.$filter = $filter;
      this.$translate = $translate;
      this.Stats = Stats;
      this.Work = Work;

      this.Auth = Auth;
      this.Notes = Notes;
      this.ConnectionQuality = ConnectionQuality;
      this.OvertimeApplications = OvertimeApplications;

      this.state = 'loading';
      this.applications = {};

      this.viewType = this.$location.path().includes('/management')
        ? 'management'
        : this.$location.path().includes('/me')
        ? 'employee'
        : 'team';
      this.isEmployeeView = this.viewType === 'employee';
      this.applicationFormVisible = false;
      this.waitingForAcceptanceApplications = [];
      this.acceptedApplications = [];
      this.requestedChangesApplications = [];
      this.startedApplications = [];
      this.finishedAndRejectedApplications = [];
      this.settledOrAllocatedApplications = [];
      this.employeesForSelect = [];

      this.managementApplicationsCount = 0;
      this.teamLeaderApplicationsCount = 0;
      this.deputyTeamLeaderApplicationsCount = 0;
      this.teamsEmployeeApplicationCount = 0;

      this.statusTypes = {
        New: 'New',
        Sent: 'Sent',
        ToAccept: 'To accept',
        Accepted: 'Accepted',
        UpdateDone: 'Update done',
        RequestedUpdate: 'Requested update',
        NewUpdate: 'New update',
        CompletedPartially: 'Completed partially',
        CompletedPositively: 'Completed positively',
        CompletedNegatively: 'Completed negatively',
        AllocatedPartially: 'Allocated Partially',
        Allocated: 'Allocated',
        Settled: 'Settled',
        SettledPartially: 'Settled partially',
        Rejected: 'Rejected'
      };
      this.acceptedApplicationStatuses = ['Accepted', 'UpdateDone', 'NewUpdate'];
      this.preferencesDictionary = {
        managementLevel: "Management",
        teamLeaderLevel: "TeamLeader",
        deputyTeamLeaderLevel: "DeputyTeamLeader",
        employeeLevel: "Employee"
      };

      this.applicationsWorklogsMap = {};
      this.worklogsApplicationId = null;

      const timeout = this.$interval(() => {
        this.notes = this.Notes.getNotes();
      }, 10);

      this.$scope.$on('$destroy', () => {
        this.$interval.cancel(timeout);
      });

      $scope.$on('connectionQualityChanged', () => {
        if (this.ConnectionQuality.isSlow) {
          this.Notes.postNote({
            type: 'slowConnection',
            manualHide: true
          });
        }
        else {
          const note = this.Notes.getNotes().find(note => note.type === 'slowConnection' && note.active);
          if (note)
            this.Notes.hide(note);
        }
      });

      this.Auth.authorize().then(
        (user) => {
          this.user = user;
          this.hasErpProjectsFlag = user.preferences.featureFlags.some(ff => ff === "ProjectsFromErpSystem");
          this.allPendingRequests = this.user.preferences.mvApplicationOvertime && this.user.preferences.mvApplicationOvertime.allPendingRequests;

          if (this.viewType === 'management')
            this.$q.all([this.Stats.getSubordinateTeams(), this.Work.getViewLevelSettings()]).then(
              (success) => {
                this.subTeams = success[0].data
                  .filter(sub =>
                    this.user.teamId !== '49a6dcff-c4e6-443e-9627-597070a68c8e' ? sub.id !== '49a6dcff-c4e6-443e-9627-597070a68c8e' : true
                  ).sort((a, b) => a.name.localeCompare(b.name));
                this.noViewLevelSettings = success[1].status === 204;

                if (this.noViewLevelSettings) {
                  this.managementMode = true;
                  this.managementLevel = true;
                  this.state = 'loaded';
                } else {
                  if (!this.allPendingRequests) this.setPreferencesInfo(success[1].data);
                  this.getApplicationsData();
                }
              }, (res) => {
                this.state = "error";
                this.error = res;
                this.Notes.postNote({
                  type: "error",
                  message: res.data
                });
              }
            );
          else
            this.getApplicationsData();
        },
        () => {
          this.Auth.handleUnauthorized();
        }
      );

      $scope.$on("$destroy", () => {
        clearTimeout(this.updateHoursFunction);
      });
    };
  }

  errorHandler(error) {
    this.state = 'error';
    this.error = error;
    // eslint-disable-next-line no-console
    console.error(error);
    let message;
    if (error.data && error.data.message && error.data.message === "User updated based on no longer current overtime application")
      message = this.$translate.instant("OVERTIME_APPLICATION.LAST_UPDATE_ERROR");
    this.Notes.postNote({
      type: 'error',
      message: message || error.data.message
    });
  }

  convertDuration(duration) {
    const hours = Math.floor(duration.asHours());
    const mins  = Math.floor(duration.asMinutes()) - hours * 60;
    const sec   = Math.floor(duration.asSeconds()) - hours * 60 * 60 - mins * 60;

    const timePart = (timePart) => {
      return timePart < 10 ? `0${timePart}` : timePart;
    };

    return `${timePart(Math.floor(hours))}:${timePart(mins)}:${timePart(sec)}`;
  }

  getApplicationsData() {
    const viewType = this.viewType === 'management' ? 'Management' : 'Team';
    const allPendingRequests = this.viewType === 'management' ? this.allPendingRequests : null;
    let requests = [
      this.OvertimeApplications.getApplications(this.isEmployeeView, viewType, allPendingRequests),
      this.isEmployeeView
        ? this.OvertimeApplications.employeeFormData()
        : this.OvertimeApplications.managerOrTLSubordinateUsers(viewType)
    ];

    this.$q.all(requests).then(
      (success) => {
        this.applications = {};
        this.waitingForAcceptanceApplications = [];
        this.acceptedApplications = [];
        this.requestedChangesApplications = [];
        this.startedApplications = [];
        this.finishedAndRejectedApplications = [];
        this.settledOrAllocatedApplications = [];

        if (this.isEmployeeView) {
          this.clients = success[1].data.clients
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(client => {
              client.displayName = (client.additionalInfo ? `(${client.additionalInfo}) ` : "") + client.name;
              return client;
            });
          this.supervisors = success[1].data.supervisors
            .filter(supervisor => !supervisor.blockedAt && (this.user.teamId !== '49a6dcff-c4e6-443e-9627-597070a68c8e' ? supervisor.teamId !== '49a6dcff-c4e6-443e-9627-597070a68c8e' : true))
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          this.firstValidStartAtDate = success[1].data.firstValidStartAtDate;

          this.approvers = angular.copy(this.supervisors).filter(supervisor => !supervisor.blockedAt).sort((supervisorA, supervisorB) => {
            const roleOrderA = ROLE_ORDER[supervisorA.role] || 2;
            const roleOrderB = ROLE_ORDER[supervisorB.role] || 2;

            if (roleOrderA !== roleOrderB) {
                return roleOrderA - roleOrderB;
            } else {
                return supervisorA.fullName.localeCompare(supervisorB.fullName);
            }
          }).map(supervisor => supervisor.fullName).join(", ");
        } else {
          this.employees = success[1].data
            .filter(employee => this.user.teamId !== '49a6dcff-c4e6-443e-9627-597070a68c8e' ? employee.teamId !== '49a6dcff-c4e6-443e-9627-597070a68c8e' : true)
            .sort((a, b) => a.fullName.localeCompare(b.fullName));

          this.employeesForSelect = angular.copy(this.employees).filter(employee => !employee.blockedAt && !employee.role.includes('Manager'));
        }

        const applications = this.isEmployeeView ? success[0].data : success[0].data.applicationsViews;
        applications.forEach(application => {
          const lastComment = application.comments.sort((a, b) => b.commentAt.localeCompare(a.commentAt))[0];
          const userFullName = this.isEmployeeView ? null : this.employees.find(employee => employee.id === application.userId).fullName;

          this.applications[application.id] = {
            ...application,
            comments: application.comments.map(comment => ({...comment, commentAt: moment(comment.commentAt).format("YYYY-MM-DD HH:mm:ss")})),
            lastComment: lastComment ? lastComment.comment : null,
            duration: this.$filter("hms")(application.duration),
            reportedDuration: this.$filter("hms")(application.reportedDuration),
            userFullName: userFullName
          };
        });

        if (viewType === 'Management') {
          let employeesApplicationsToAcceptCounter = 0;

          Object.keys(this.preferencesDictionary).forEach(key => this[key + 'ForCounter'] = this[key] ? true : null);

          if (this.employeeLevelTeam) {
            const employeesIdsFromCurrentLevel = this.employees
              .filter(employee => employee.teamId === this.employeeLevelTeam && employee.role === 'Employee')
              .map(employee => employee.id);
            employeesApplicationsToAcceptCounter = Object.values(this.applications).filter(application =>
                employeesIdsFromCurrentLevel.includes(application.userId) && ['ToAccept', 'RequestedUpdate'].includes(application.status)
              ).length;
          }

          Object.entries(success[0].data.applicationViewLevelStatus).forEach(([key, value]) =>
            this[key] = key === 'teamsEmployeeApplicationCount'
              ? Object.values(value).reduce((prev, curr) => prev + curr, 0) - employeesApplicationsToAcceptCounter
              : value
          );
        }

        Object.values(this.applications).forEach(application => {
          if (application.status.includes('Completed') || application.status.includes('Rejected'))
            this.finishedAndRejectedApplications.push(application);
          else if (application.status === 'Allocated' || application.status === 'AllocatedPartially' || application.status === 'Settled' || application.status === 'SettledPartially')
            this.settledOrAllocatedApplications.push(application);
          else if (moment.duration(application.reportedDuration).asMilliseconds > 0)
            this.startedApplications.push(application);
          else if (application.status === 'RequestedUpdate') {
            if (this.isEmployeeView)
              this.waitingForAcceptanceApplications.push(application);
            else
              this.requestedChangesApplications.push(application);
          } else if (application.status === 'UpdateDone' && !application.employeeAccepted) {
            this.waitingForAcceptanceApplications.push(application);
          } else if (this.acceptedApplicationStatuses.includes(application.status) && application.acceptedBy)
            this.acceptedApplications.push(application);
          else
            this.waitingForAcceptanceApplications.push(application);
        });

        if (this.managementMode)
          this.managementMode = false;

        this.state = 'loaded';
      },
      (error) => this.errorHandler(error)
    );
  }

  closeForm() {
    this.applicationFormVisible = false;
    this.editFormValues = null;
    this.editApplicationStarted = false;

    if (!this.isEmployeeView)
      this.clients = null;

    if (this.viewMode) {
      this.viewMode = false;
      this.state = 'loading';
      this.getApplicationsData();
    }
  }

  createApplication(data) {
    if (this.state === 'loading')
      return;

    this.state = 'loading';

    if (this.isEmployeeView) {
      data.userId = this.user.id;
    }

    data.duration = moment.duration(data.duration, 'hours');

    this.OvertimeApplications.createApplication(data, this.isEmployeeView).then(
      () => {
        this.closeForm();
        this.getApplicationsData();
      },
      (error) => this.errorHandler(error)
    );
  }

  setEditValues(data, clients, firstValidStartAtDate) {
    this.editFormValues = angular.copy(data);
    this.editFormValues.clients = data.clients.map(dataClient => clients.find(client => client.id === dataClient.id));
    this.editFormValues.firstValidStartAtDate = firstValidStartAtDate;
    if (!this.isEmployeeView)
      this.editFormValues.users = this.employees.filter(employee => employee.id === data.userId);

    this.applicationFormVisible = true;
  }

  startEditApplication(data) {
    if (this.editApplicationStarted)
      return;

    this.editApplicationStarted = true;

    if (this.isEmployeeView)
      this.setEditValues(data, this.clients);
    else
      this.OvertimeApplications.managerOrTLCreateView(data.userId).then(
        (success) => {
          this.clients = success.data.clients
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(client => {
              client.displayName = (client.additionalInfo ? `(${client.additionalInfo}) ` : "") + client.name;
              return client;
            });
          this.firstValidStartAtDate = success.data.firstValidStartAtDate;
          this.setEditValues(data, success.data.clients, success.data.firstValidStartAtDate);
        },
        (error) => this.errorHandler(error)
      );
  }

  editApplication(data) {
    if (this.state === 'loading')
      return;

    this.state = 'loading';

    if (this.isEmployeeView && data.id && !data.employeeAccepted && (!data.comment || data.comment === '')) {
      this.closeForm();
      this.getApplicationsData();
      return;
    }

    const dataToSend = {
      clients: data.clients,
      comment: data.comment,
      duration: moment.duration(data.duration, 'hours'),
      endAt: data.endAt,
      startAt: data.startAt,
      lastUpdate: data.eventAt
    };

    const request = data.requestChanges
      ? this.OvertimeApplications.commentApplication(data.id, dataToSend)
      : this.OvertimeApplications.editApplication(data.id, dataToSend, this.isEmployeeView);

    request.then(
      () => {
        this.closeForm();
        this.getApplicationsData();
      },
      (error) => this.errorHandler(error)
    );
  }

  startDeleteApplication(application) {
    this.applicationIdToDelete = application.id;
  }

  deleteApplication() {
    this.state = 'loading';

    this.OvertimeApplications.deleteApplication(this.applicationIdToDelete, this.isEmployeeView).then(
      () => {
        this.applicationIdToDelete = null;
        this.getApplicationsData();
      },
      (error) => this.errorHandler(error)
    );
  }

  printClients(clients) {
    return clients.map(client => client.name).join(', ');
  }

  printStatus(status) {
    return this.statusTypes[status];
  }

  startAcceptApplication(application) {
    this.applicationIdToAccept = application.id;
  }

  acceptApplication() {
    this.state = 'loading';

    this.OvertimeApplications.acceptsApplication(this.applicationIdToAccept, this.applications[this.applicationIdToAccept], this.user.id).then(
      () => {
        this.applicationIdToAccept = null;
        this.getApplicationsData();
      },
      (error) => this.errorHandler(error)
    );
  }

  startAllocateApplication(application) {
    this.overtimeUserModal = application;
    this.showAllocateModal = true;
  }

  startSettleApplication(application) {
    this.state = "loading";
    this.OvertimeApplications.getSettlements(application.id).then(
      (success) => {
        this.state = "loaded";
        const timeToSettle =
          moment.duration(success.data.overtimeDuration).valueOf()
          - success.data.settlements.reduce(
              (accu, b) =>
                accu + moment.duration(b.settledApplicationOvertime).valueOf()
              , 0
            );
        application.timeToSettle = this.$filter("hms")(moment.duration(timeToSettle).toJSON());
        this.overtimeUserModal = application;
        this.showSettleModal = true;
      },
      (error) => this.errorHandler(error)
    );
  }

  closeSettleModal() {
    this.showSettleModal = false;
    this.overtimeUserModal = null;

    clearTimeout(this.updateHoursFunction);
    this.timeToSettleFormatInvalid = null;
    this.timeToSettleIsBigger = null;
    this.timeToSettleIsTooSmall = null;
    this.timeToSettle = null;
  }

  printApplicationNumber(id) {
    return this.applications.find(application => application.id === id).applicationCode;
  }

  viewApplication(application) {
    this.viewMode = true;
    application.requestChanges = true;
    this.OvertimeApplications.editApplication(application.id, {employeeAccepted: true, lastUpdate: application.eventAt}, this.isEmployeeView).then(
      (success) => {
        application = success.data;
        application.requestChanges = true;
        application.duration = this.$filter("hms")(moment.duration(application.duration).toJSON());
        this.setEditValues(application, this.clients);
      },
      (error) => this.errorHandler(error)
    );
  }

  setPreferencesInfo(data) {
    Object.entries(this.preferencesDictionary).forEach(([key, value]) => {
      if (data.viewLevels.includes(value))
        this[key] = true;
    });
    this.employeeLevelTeam = data.teamId;
  }

  showData() {
    this.state = "loading";

    const data = {
      viewLevels: []
    };

    Object.keys(this.preferencesDictionary).forEach(key => {
      if (this[key])
        data.viewLevels.push(this.preferencesDictionary[key]);
    });

    if (this.employeeLevel) {
      data.teamId = this.employeeLevelTeam;
    }

    const previousAllPendingRequests = this.user.preferences.mvApplicationOvertime && this.user.preferences.mvApplicationOvertime.allPendingRequests;

    if (this.allPendingRequests !== previousAllPendingRequests)
      this.Auth.getPreferences().then((preferences) => {
        var newPreferences = angular.copy(preferences);
        newPreferences.mvApplicationOvertime = {
          allPendingRequests: this.allPendingRequests
        };
        this.Auth.patchPreferences(newPreferences).then(() => {
          this.user.preferences = angular.fromJson(localStorage.getItem("ngStorage-roedl-session")).preferences;
        });
      });

    if (this.allPendingRequests)
      return this.getApplicationsData();

    this.Work.editViewLevelSettings(data).then(
      (success) => {
        this.setPreferencesInfo(success.data);

        if (this.noViewLevelSettings) {
          this.managementMode = false;
          this.noViewLevelSettings = false;
        }

        this.getApplicationsData();
      },
      (res) => {
        this.Notes.postNote({
          type: "error",
          message: res.data
        });
      }
    );
  }

  refreshAfterAllocation() {
    this.state = "loading";
    this.getApplicationsData();
  }

  startRejectApplication(application) {
    this.applicationIdToReject = application.id;
  }

  rejectApplication() {
    this.state = 'loading';
    const data = {
      rejectedBy: this.user.id,
      comment: this.comment,
      lastUpdate: this.applications[this.applicationIdToReject].eventAt
    };

    this.OvertimeApplications.rejectApplication(this.applicationIdToReject, data).then(
      () => {
        this.applicationIdToReject = null;
        this.comment = null;
        this.getApplicationsData();
      },
      (error) => this.errorHandler(error)
    );
  }

  toggleEmployeeLevelOptions(source) {
    if (!source)
      this.allPendingRequests = false;
    else
      Object.keys(this.preferencesDictionary).forEach(key => this[key] = false);
  }

  extrasForName(subTeam) {
    const extraValues = [];
    if (subTeam.excludeFromProfitabilityReport)
      extraValues.push(`${this.$translate.instant("WORK_DATA.DISPLAY_PREFERENCES.EXCLUDED_FROM_PROFITABILITY_VIEW")}`);
    
    if (subTeam.archivedAt)
      extraValues.push(`(${this.$translate.instant("TEAMS.ARCHIVED")})`);

    return extraValues.join(" ");
  }

  showModalWithWorklogs(id, applicationCode) {
    if (this.applicationsWorklogsMap[id]) {
      this.worklogsToShow = this.applicationsWorklogsMap[id].worklogs;
      this.worklogsApplicationId = id;
      return;
    }

    this.state = 'loading';

    this.OvertimeApplications.getWorklogsForApplication(id).then(
      ({ data }) => {
        this.applicationsWorklogsMap[id] = {
          worklogs: data.map(worklog => ({...worklog, duration: moment.duration(worklog.duration)})),
          code: applicationCode
        };
        this.worklogsToShow = this.applicationsWorklogsMap[id].worklogs;
        this.worklogsApplicationId = id;
        this.state = 'loaded';
      },
      (error) => this.errorHandler(error)
    );
  }

  closeApplicationWorklogsModal() {
    this.worklogsToShow = null;
    this.worklogsApplicationId = null;
  }

  formatNumberToTime(hours) {
    const formattedHours = hours.replaceAll(',', '.');
    const duration = moment.duration(formattedHours, 'hours');

    const timePart = (timePart) => {
      return timePart < 10 ? `0${timePart}` : timePart;
    };

    return `${timePart(Math.floor(duration.asHours()))}:${timePart(duration.get('minutes'))}:${timePart(duration.get('seconds'))}`;
  }

  updateDuration() {
    clearTimeout(this.updateHoursFunction);
    this.updateHoursFunction = setTimeout(() => {
      const format = /^(\d+:[0-5]\d:[0-5]\d|\d*([,.]\d*)|\d*)$/;
      const numberFormat = /^(\d*([,.]\d*)|\d*)$/;
      const match = this.timeToSettle.match(format);

      if (!match) {
        this.timeToSettleFormatInvalid = true;
        return;
      } else {
        if (this.timeToSettle.match(numberFormat))
          this.timeToSettle = this.formatNumberToTime(this.timeToSettle);
      }

      if (moment.duration(this.timeToSettle).asMilliseconds() > moment.duration(this.overtimeUserModal.timeToSettle).asMilliseconds()) {
        this.timeToSettleIsBigger = true;
        return;
      }

      if (moment.duration(this.timeToSettle).asMilliseconds() <= 0) {
        this.timeToSettleIsTooSmall = true;
        return;
      }

      this.timeToSettleFormatInvalid = false;
      this.timeToSettleIsBigger = false;
      this.timeToSettleIsTooSmall = false;
    }, 500);
  }

  settleApplication() {
    this.state = 'loading';

    this.OvertimeApplications.saveSettlement(
      this.overtimeUserModal.id,
      {applicationOvertimeToSettle: moment.duration(this.timeToSettle).toJSON()}
    )
      .then(() => {
        this.closeSettleModal();
        this.getApplicationsData();
      }, (error) => this.errorHandler(error));
  }

  useAllTime() {
    this.timeToSettle = this.overtimeUserModal.timeToSettle;
  }
}
